import {
  useRef,
  useMemo,
  useEffect,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import type { RepoStackAnalysis, KBNode, DetectedTool, SdlcStage } from '../types';
import { STAGES, STAGE_LABELS } from '../types';
import {
  STAGE_ICONS,
  OVERVIEW_EDGE_TYPES,
  EDGE_STYLE,
  EDGE_LABEL,
  type ColumnData,
} from './AppMapGraph.constants';
import { ToolCard, GapCard, EmptySlot, LegendSwatch } from './AppMapGraph.parts';
import { useLanguageFilter } from '../hooks/useLanguageFilter';

// Public handle kept for API compatibility; zoom is now a no-op — the canvas
// scrolls naturally and reshapes on focus.
export interface AppMapHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;
}

interface AppMapGraphProps {
  data: RepoStackAnalysis;
  selectedToolId: string | null;
  onToolSelect: (toolId: string) => void;
  onToolHover?: (toolId: string | null, event?: MouseEvent) => void;
  selectedLanguageIds?: Set<string>;
}

export const AppMapGraph = forwardRef<AppMapHandle, AppMapGraphProps>(
  ({ data, selectedToolId, onToolSelect, onToolHover, selectedLanguageIds }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [edgeGeometry, setEdgeGeometry] = useState<
      Array<{
        id: string;
        d: string;
        type: string;
        emphasized: boolean;
        labeled: boolean;
        midX: number;
        midY: number;
      }>
    >([]);
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        zoomIn: () => {},
        zoomOut: () => {},
        zoomFit: () => {
          containerRef.current?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        },
      }),
      [],
    );

    const { isLanguageMatch } = useLanguageFilter({
      nodes: data.graph.nodes,
      edges: data.graph.edges,
      selectedLanguageIds,
    });

    const columns = useMemo<ColumnData[]>(() => {
      const nodeMap: Record<string, KBNode> = {};
      for (const n of data.graph.nodes) nodeMap[n.id] = n;
      const detMap: Record<string, DetectedTool> = {};
      for (const d of data.detectedTools) detMap[d.id] = d;

      const bucket: Record<SdlcStage, ColumnData> = {} as Record<SdlcStage, ColumnData>;
      for (const s of STAGES) bucket[s] = { stage: s, tools: [], gaps: [] };

      for (const d of data.detectedTools) {
        const n = nodeMap[d.id];
        if (!n) continue;
        if (!isLanguageMatch(d.id)) continue;
        bucket[n.sdlcStage].tools.push({ node: n, det: d });
      }
      for (const r of data.recommendations) {
        const n = nodeMap[r.suggested];
        if (!n) continue;
        if (detMap[n.id]) continue;
        if (!isLanguageMatch(r.suggested)) continue;
        bucket[n.sdlcStage].gaps.push({ node: n, priority: r.priority, reason: r.reason });
      }

      const tierOrder = { detected: 0, likely: 1, possible: 2 } as const;
      for (const s of STAGES) {
        bucket[s].tools.sort((a, b) => {
          const t = tierOrder[a.det.tier] - tierOrder[b.det.tier];
          return t !== 0 ? t : (b.node.connections || 0) - (a.node.connections || 0);
        });
        const prOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
        bucket[s].gaps.sort(
          (a, b) => (prOrder[a.priority || 'medium'] ?? 1) - (prOrder[b.priority || 'medium'] ?? 1),
        );
      }

      return STAGES.map((s) => bucket[s]);
    }, [data, isLanguageMatch]);

    const detectedIds = useMemo(
      () => new Set(data.detectedTools.map((d) => d.id)),
      [data.detectedTools],
    );
    const gapIds = useMemo(() => {
      const s = new Set<string>();
      for (const r of data.recommendations) {
        if (!detectedIds.has(r.suggested)) s.add(r.suggested);
      }
      return s;
    }, [data.recommendations, detectedIds]);

    const activeId = selectedToolId || hoveredId;
    const neighborhood = useMemo(() => {
      if (!activeId) return null;
      const set = new Set<string>([activeId]);
      for (const e of data.graph.edges) {
        if (e.type === 'language' || e.type === 'ecosystem') continue;
        if (e.source === activeId) set.add(e.target);
        if (e.target === activeId) set.add(e.source);
      }
      return set;
    }, [activeId, data.graph.edges]);

    const recomputeEdges = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const crect = canvas.getBoundingClientRect();

      const out: Array<{
        id: string;
        d: string;
        type: string;
        emphasized: boolean;
        labeled: boolean;
        midX: number;
        midY: number;
      }> = [];

      for (let i = 0; i < data.graph.edges.length; i++) {
        const e = data.graph.edges[i];
        if (e.type === 'language' || e.type === 'ecosystem') continue;

        let visible = false;
        let emphasized = false;
        if (activeId) {
          visible = e.source === activeId || e.target === activeId;
          emphasized = visible;
        } else {
          if (OVERVIEW_EDGE_TYPES.has(e.type)) {
            visible = true;
          } else if (e.type === 'pairs_with') {
            const srcDet = detectedIds.has(e.source);
            const tgtDet = detectedIds.has(e.target);
            const srcGap = gapIds.has(e.source);
            const tgtGap = gapIds.has(e.target);
            visible = (srcDet && tgtGap) || (tgtDet && srcGap);
          }
        }

        if (!visible) continue;

        const a = cardRefs.current[e.source];
        const b = cardRefs.current[e.target];
        if (!a || !b) continue;

        const ar = a.getBoundingClientRect();
        const br = b.getBoundingClientRect();

        const sourceRight = ar.right < br.left;
        const ax = (sourceRight ? ar.right : ar.left) - crect.left;
        const bx = (sourceRight ? br.left : br.right) - crect.left;
        const ay = ar.top + ar.height / 2 - crect.top;
        const by = br.top + br.height / 2 - crect.top;

        const dx = bx - ax;
        const curvature = Math.min(120, Math.abs(dx) * 0.4);
        const c1x = ax + (sourceRight ? curvature : -curvature);
        const c2x = bx - (sourceRight ? curvature : -curvature);

        const midX = 0.125 * ax + 0.375 * c1x + 0.375 * c2x + 0.125 * bx;
        const midY = (ay + by) / 2;
        const labeled = e.type === 'conflicts_with';

        out.push({
          id: `${i}`,
          d: `M${ax},${ay} C${c1x},${ay} ${c2x},${by} ${bx},${by}`,
          type: e.type,
          emphasized,
          labeled,
          midX,
          midY,
        });
      }

      setEdgeGeometry(out);
    }, [data.graph.edges, activeId, detectedIds, gapIds]);

    useEffect(() => {
      recomputeEdges();
      const container = containerRef.current;
      if (!container) return;

      const ro = new ResizeObserver(recomputeEdges);
      ro.observe(container);
      if (canvasRef.current) ro.observe(canvasRef.current);
      for (const el of Object.values(cardRefs.current)) if (el) ro.observe(el);

      const onScroll = () => recomputeEdges();
      container.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', recomputeEdges);

      return () => {
        ro.disconnect();
        container.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', recomputeEdges);
      };
    }, [recomputeEdges, columns]);

    const handleCardEnter = useCallback(
      (id: string, ev: React.MouseEvent) => {
        setHoveredId(id);
        onToolHover?.(id, ev.nativeEvent);
      },
      [onToolHover],
    );
    const handleCardLeave = useCallback(() => {
      setHoveredId(null);
      onToolHover?.(null);
    }, [onToolHover]);

    return (
      <div
        ref={containerRef}
        className="relative w-full h-full overflow-auto bg-[#0a0e1a]"
        onClick={(e) => {
          if (e.target === e.currentTarget) onToolSelect('');
        }}
      >
        {/* Stage header — compact row, no large circles */}
        <div className="sticky top-0 z-20 bg-[#0a0e1a]/95 backdrop-blur-sm min-w-[1100px] px-[50px] pt-5 pb-4 border-b border-slate-800/60">
          <div className="grid gap-3 relative grid-cols-8">
            {columns.map((col) => {
              const Icon = STAGE_ICONS[col.stage];
              const gap = col.tools.length === 0;
              const toolCount = col.tools.length;
              const sugCount = col.gaps.length;
              return (
                <div key={col.stage} className="flex flex-col px-1 select-none">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon
                      className={`w-3.5 h-3.5 flex-shrink-0 ${gap ? 'text-amber-500/80' : 'text-slate-400'}`}
                    />
                    <span
                      className={`text-[11px] font-semibold uppercase tracking-wider ${
                        gap ? 'text-amber-400/90' : 'text-slate-300'
                      }`}
                    >
                      {STAGE_LABELS[col.stage]}
                    </span>
                  </div>
                  <div className="text-[10.5px] text-slate-500 font-mono pl-5">
                    {gap ? (
                      <span className="text-amber-500/80">no tools</span>
                    ) : (
                      <>
                        {toolCount} tool{toolCount !== 1 ? 's' : ''}
                      </>
                    )}
                    {sugCount > 0 && (
                      <span className="text-teal-400/80 ml-1.5">· {sugCount} suggested</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div ref={canvasRef} className="relative min-w-[1100px] px-[50px] py-6">
          <svg
            className="absolute inset-0 pointer-events-none"
            style={{ width: '100%', height: '100%' }}
          >
            <defs>
              <marker
                id="arrow-emph"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 z" fill="#cbd5e1" />
              </marker>
            </defs>
            {edgeGeometry.map((e) => {
              const style = EDGE_STYLE[e.type] || { color: '#475569', weight: 1 };
              const opacity = e.emphasized ? 0.85 : activeId ? 0.06 : 0.22;
              return (
                <path
                  key={e.id}
                  d={e.d}
                  fill="none"
                  stroke={style.color}
                  strokeWidth={e.emphasized ? style.weight + 0.5 : style.weight}
                  strokeDasharray={style.dash || undefined}
                  strokeOpacity={opacity}
                  markerEnd={e.emphasized ? 'url(#arrow-emph)' : undefined}
                />
              );
            })}
            {edgeGeometry.map((e) => {
              if (!e.labeled) return null;
              const label = EDGE_LABEL[e.type];
              if (!label) return null;
              const isConflict = e.type === 'conflicts_with';
              const fill = isConflict ? '#fca5a5' : '#cbd5e1';
              const halfW = label.length * 3.1 + 6;
              return (
                <g key={`lbl-${e.id}`} transform={`translate(${e.midX},${e.midY})`}>
                  <rect
                    x={-halfW}
                    y={-7}
                    width={halfW * 2}
                    height={14}
                    rx={3}
                    fill="#0a0e1a"
                    fillOpacity={0.9}
                    stroke={isConflict ? 'rgba(248,113,113,0.55)' : 'rgba(71,85,105,0.5)'}
                    strokeWidth={0.5}
                  />
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={9}
                    fontFamily="ui-monospace, monospace"
                    fill={fill}
                    letterSpacing={0.2}
                  >
                    {label}
                  </text>
                </g>
              );
            })}
          </svg>

          <div className="grid gap-3 relative grid-cols-8">
            {columns.map((col) => {
              const hasContent = col.tools.length + col.gaps.length > 0;
              return (
                <div key={col.stage} data-stage-col={col.stage} className="flex flex-col gap-2">
                  {col.tools.map(({ node, det }) => (
                    <ToolCard
                      key={node.id}
                      node={node}
                      det={det}
                      stage={col.stage}
                      selected={selectedToolId === node.id}
                      dimmed={neighborhood !== null && !neighborhood.has(node.id)}
                      onEnter={(e) => handleCardEnter(node.id, e)}
                      onLeave={handleCardLeave}
                      onClick={() => onToolSelect(node.id)}
                      refCb={(el) => {
                        cardRefs.current[node.id] = el;
                      }}
                    />
                  ))}
                  {col.gaps.map(({ node, priority }) => (
                    <GapCard
                      key={node.id}
                      node={node}
                      priority={priority}
                      stage={col.stage}
                      selected={selectedToolId === node.id}
                      dimmed={neighborhood !== null && !neighborhood.has(node.id)}
                      onEnter={(e) => handleCardEnter(node.id, e)}
                      onLeave={handleCardLeave}
                      onClick={() => onToolSelect(node.id)}
                      refCb={(el) => {
                        cardRefs.current[node.id] = el;
                      }}
                    />
                  ))}
                  {!hasContent && <EmptySlot />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom legend — trimmed to the only two relationships shown by default */}
        <div className="sticky bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/95 to-transparent px-6 pt-6 pb-3">
          <div className="flex items-center gap-5 text-[10.5px]">
            <span className="text-slate-500 uppercase tracking-wider font-semibold">
              Relationships
            </span>
            <LegendSwatch color="#94a3b8" label="depends on" />
            <LegendSwatch color="#f87171" dash="4,3" label="conflicts" />
            <span className="text-slate-600">·</span>
            <span className="text-slate-500">Hover or click a card to reveal more</span>
            <span className="ml-auto text-slate-500">
              {activeId ? 'Neighborhood — click canvas to clear' : ''}
            </span>
          </div>
        </div>
      </div>
    );
  },
);

AppMapGraph.displayName = 'AppMapGraph';
